import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import FeatureCarousel from "../About/components/FeatureCarousel";
import { featureCardData } from "../About/data/featureCardData";
import UnlockingIntelligence from "../About/components/UnlockingIntelligence";
import { companyLogos } from "./data/companyLogos";

import { Zap, MoveRight, Play } from "lucide-react";

function Home() {
  return (
    <>
      <div className="bg-dark-bg">
        <div className="min-h-screen flex items-center justify-center px-5 md:px-0">
          <div className="container mx-auto w-full max-w-5xl text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="text-5xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight font-bold mt-5"
            >
              <span className="bg-gradient-text bg-clip-text text-transparent">
                Transform Chemical Data <br /> Into Strategic Intelligence
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              className="mt-6 md:mt-8 text-white/90 text-base sm:text-lg md:text-xl max-w-4xl mx-auto leading-relaxed px-2"
            >
              Enterprise-grade AI platform that structures and retrieves
              insights from complex chemical archives. Unlock decades of
              institutional knowledge with contextual precision.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              className="mt-8 md:mt-12 flex flex-col sm:flex-row flex-wrap gap-4 md:gap-5 justify-center"
            >
              <Link
                to="/pricingPlans"
                className="inline-flex items-center justify-center bg-gradient-to-r from-[#00A3FF] to-[#0077CC] hover:shadow-xl hover:shadow-[#00A3FF]/40 rounded-lg px-6 py-3 sm:px-8 sm:py-4 text-white text-base sm:text-lg font-semibold transition-all duration-200 hover:scale-105"
              >
                Get Started <MoveRight className="ml-2" size={20} />
              </Link>
              <Link
                to="https://www.youtube.com/watch?v=I4h8H3v0-rQ"
                target="_blank"
                className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-6 py-3 sm:px-8 sm:py-4 text-white text-base sm:text-lg font-semibold transition-all duration-200 hover:scale-105"
              >
                Watch Demo <Play className="ml-2" size={20} />
              </Link>
            </motion.div>

            {/* Analytics Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
              className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-4xl mx-auto"
            >
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  500+
                </div>
                <div className="text-white/60 text-sm md:text-base">
                  Active Users
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  1M+
                </div>
                <div className="text-white/60 text-sm md:text-base">
                  Documents Processed
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  10B+
                </div>
                <div className="text-white/60 text-sm md:text-base">
                  Data Points Analyzed
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">
                  99.9%
                </div>
                <div className="text-white/60 text-sm md:text-base">
                  Accuracy Rate
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Trusted By Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: "easeOut" }}
          className="w-full py-16 overflow-hidden"
        >
          <div className="text-center mb-8">
            <h3 className="text-white/60 text-sm md:text-base uppercase tracking-wider font-semibold">
              Trusted By Leading Organisations
            </h3>
          </div>

          <div className="relative">
            <div className="flex animate-scroll">
              {/* First set of logos */}
              <div className="flex items-center gap-12 md:gap-16 px-8 shrink-0">
                {companyLogos.map((company) => (
                  <div
                    key={`logo-1-${company.id}`}
                    className="flex items-center justify-center w-40 h-20 md:w-48 md:h-24"
                  >
                    <img
                      src={company.logo}
                      alt={company.alt}
                      className="h-12 md:h-16 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
                    />
                  </div>
                ))}
              </div>

              {/* Duplicate set for seamless loop */}
              <div className="flex items-center gap-12 md:gap-16 px-8 shrink-0">
                {companyLogos.map((company) => (
                  <div
                    key={`logo-2-${company.id}`}
                    className="flex items-center justify-center w-40 h-20 md:w-48 md:h-24"
                  >
                    <img
                      src={company.logo}
                      alt={company.alt}
                      className="h-12 md:h-16 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* hidden intelligence */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mt-24 bg-dark-bg"
          id="why"
        >
          <div className="container mx-auto w-full max-w-4xl px-5 md:px-0 mb-12">
            <UnlockingIntelligence />
          </div>
        </motion.section>

        {/* how it works  */}
        <section className="mt-16 pb-16">
          <div className="container mx-auto w-full px-5 md:px-0">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="text-center mb-12"
            >
              <h2 className="font-bold text-white text-3xl md:text-4xl">
                How we revive information's potential
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="flex justify-center items-center"
            >
              <div className="w-full max-w-6xl">
                <FeatureCarousel featureCards={featureCardData} />
              </div>
            </motion.div>
          </div>
        </section>

        <section className="pb-16">
          <div className="container mx-auto w-full max-w-4xl px-5 md:px-0 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
              className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-[#00A3FF] to-[#0077CC] shadow-xl shadow-[#00A3FF]/30"
            >
              <Zap size={40} className="text-white" />
            </motion.div>
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
              className="font-bold text-3xl md:text-4xl mb-6 text-white"
            >
              Amplify your data. Accelerate your research.
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
              className="text-white/80 text-lg md:text-lg mb-10 max-w-3xl mx-auto leading-relaxed"
            >
              Stop sifting, start researching. Data Revival provides the
              AI-powered lens to bring clarity and structure to your vast
              swathes of locked data. We empower you to harness the full
              potential of your chemical data, efficiently and effectively.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5, ease: "easeOut" }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-gradient-to-r from-[#00A3FF] to-[#0077CC] hover:shadow-xl hover:shadow-[#00A3FF]/40 rounded-lg px-10 py-5 text-white text-lg font-semibold transition-all duration-200 hover:scale-105"
              >
                Learn how we can help you
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Home;
